// excel.service.ts

import { Injectable } from '@angular/core'
import * as XLSX from 'xlsx'

@Injectable({
  providedIn: 'root'
})
export class ExcelService {
  constructor() {}

  getSheetNames(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader: FileReader = new FileReader()
      reader.onload = (e: any) => {
        const data: Uint8Array = new Uint8Array(e.target.result)
        const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array' })
        const sheetNames: string[] = workbook.SheetNames
        resolve(sheetNames)
      }

      reader.onerror = e => {
        reject(e)
      }

      reader.readAsArrayBuffer(file)
    })
  }

  readDataFromSheet(file: File, sheetName: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader: FileReader = new FileReader()
      reader.onload = (e: any) => {
        const data: Uint8Array = new Uint8Array(e.target.result)
        const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array' })
        const sheet: XLSX.WorkSheet = workbook.Sheets[sheetName]
        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        resolve(jsonData)
      }

      reader.onerror = e => {
        reject(e)
      }

      reader.readAsArrayBuffer(file)
    })
  }

  jsonToSheet(jsonData: any[], sheetName: string): XLSX.WorkSheet {
    return XLSX.utils.json_to_sheet(jsonData, { skipHeader: true })
  }

  generateExcelFile(data: any[]): Blob {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data, { header: Object.keys(data[0]) })
    const workbook: XLSX.WorkBook = XLSX.utils.book_new()

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet1')

    // Generate the Excel file as a Blob
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  }

  generateExcelFileWithHeaders(headers: any[], data: []) {
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook: XLSX.WorkBook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    const columnWidths = this.calculateColumnWidths(headers, data)
    worksheet['!cols'] = columnWidths

    XLSX.writeFile(workbook, 'SOP Compliance EMPLOYEE_SUMMARY.xlsx')
  }

  sheetToJSON(worksheet: XLSX.WorkSheet, opts?: XLSX.Sheet2JSONOpts): any[] {
    return XLSX.utils.sheet_to_json(worksheet, opts)
  }

  decodeWorksheetRange(worksheet: XLSX.WorkSheet, mapping: string): XLSX.Range {
    const ref = worksheet[mapping]
    return XLSX.utils.decode_range(ref)
  }

  getCellByIndexes(worksheet: XLSX.WorkSheet, R: number, C: number): XLSX.CellObject | undefined {
    const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
    return worksheet[cellRef]
  }

  formatCell(cell: XLSX.CellObject): string {
    return XLSX.utils.format_cell(cell)
  }

  readExcelFromBinaryString(bstr: string): XLSX.WorkBook {
    const workbook: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' })
    return workbook
  }

  calculateColumnWidths(headers: string[], data: string[][]): XLSX.ColInfo[] {
    const widths: number[] = []

    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        const cellWidth = (cell.length > headers[colIndex].length ? cell.length : headers[colIndex].length) + 2
        if (!widths[colIndex] || widths[colIndex] < cellWidth * 1.2) {
          widths[colIndex] = cellWidth
        }
      })
    })
    return widths.map(width => ({ width }))
  }

  normalizeExcelDate(dateStr: string): string {
    if (!dateStr) return dateStr

    let parts = dateStr.split('/')
    if (parts.length !== 3) return dateStr

    let [dd, mm, yy] = parts.map(p => p.trim())

    if (yy.length > 4) {
      yy = yy.slice(-4)
    }

    if (yy.length === 2) {
      const yearNum = Number(yy)
      yy = yearNum < 50 ? (2000 + yearNum).toString() : (1900 + yearNum).toString()
    }

    dd = dd.padStart(2, '0')
    mm = mm.padStart(2, '0')

    return `${dd}/${mm}/${yy}`
  }
}
